import type { PosCartDiscount, PosCartItem } from './types.js';

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function itemGross(item: PosCartItem): number {
  return roundMoney(item.unitPrice * item.quantity);
}

export function itemDiscountValue(item: PosCartItem): number {
  const gross = itemGross(item);
  const pctDiscount = item.discountPct ? (gross * item.discountPct) / 100 : 0;
  const flatDiscount = item.discountAmount ?? 0;
  return roundMoney(Math.min(gross, pctDiscount > 0 ? pctDiscount : flatDiscount));
}

export function itemLineTotal(item: PosCartItem): number {
  return roundMoney(itemGross(item) - itemDiscountValue(item));
}

export function cartLineLabel(item: PosCartItem): string {
  if (item.type === 'repair') return item.serviceName ?? 'Repair';
  if (item.type === 'trade_in') return item.deviceDescription ?? 'Trade-In';
  if (item.type === 'fee') return item.name ?? item.description ?? 'Fee';
  return item.name ?? item.description ?? 'Item';
}

export function cartLineSubtitle(item: PosCartItem): string | null {
  if (item.type === 'repair' && item.modelName) return item.modelName;
  if (item.type === 'inventory' && item.sku) return item.sku;
  if (item.type === 'trade_in' && item.condition) return item.condition;
  return null;
}

export function isTaxableInventoryItem(item: PosCartItem): boolean {
  return item.type === 'inventory' && item.taxable !== false;
}

function isTaxableUnderMode(
  item: PosCartItem,
  taxMode: 'parts_only' | 'all' | 'none',
): boolean {
  if (taxMode === 'none') return false;
  if (item.type === 'trade_in') return false;
  if (taxMode === 'parts_only') return isTaxableInventoryItem(item);
  // all: inventory if taxable; repairs/fees always (unless we later flag tax-exempt)
  if (item.type === 'inventory') return item.taxable !== false;
  return item.type === 'repair' || item.type === 'fee';
}

/**
 * POS cart tax — mirrors apps/api calculateTax semantics:
 * pre-tax line totals; tax_mode controls labour vs parts.
 */
export function computeCartTotals(
  items: PosCartItem[],
  cartDiscount: PosCartDiscount | null,
  taxRate: number,
  taxMode: 'parts_only' | 'all' | 'none' = 'parts_only',
) {
  const subtotal = roundMoney(items.reduce((sum, item) => sum + itemGross(item), 0));
  const itemDiscountTotal = roundMoney(items.reduce((sum, item) => sum + itemDiscountValue(item), 0));
  const discountedSubtotal = roundMoney(items.reduce((sum, item) => sum + itemLineTotal(item), 0));
  const cartDiscountValue = (() => {
    if (!cartDiscount) return 0;
    const raw =
      cartDiscount.type === 'percent'
        ? (discountedSubtotal * cartDiscount.amount) / 100
        : cartDiscount.amount;
    return roundMoney(Math.min(discountedSubtotal, raw));
  })();
  const afterCartDiscount = roundMoney(Math.max(discountedSubtotal - cartDiscountValue, 0));

  // Allocate cart discount proportionally, then tax eligible lines only
  let taxableAmount = 0;
  if (taxMode !== 'none' && afterCartDiscount > 0 && discountedSubtotal > 0) {
    for (const item of items) {
      if (!isTaxableUnderMode(item, taxMode)) continue;
      const lineNet = itemLineTotal(item);
      const share = lineNet / discountedSubtotal;
      taxableAmount += afterCartDiscount * share;
    }
    taxableAmount = roundMoney(taxableAmount);
  }

  const tax = roundMoney(taxableAmount * taxRate);
  const total = roundMoney(afterCartDiscount + tax);

  return {
    subtotal,
    itemDiscountTotal,
    discountedSubtotal,
    cartDiscountValue,
    taxableAmount,
    tax,
    total,
    youSaved: roundMoney(itemDiscountTotal + cartDiscountValue),
  };
}

export function cartHasInventory(items: PosCartItem[]): boolean {
  return items.some((i) => i.type === 'inventory');
}

export function cartHasRepairsOnly(items: PosCartItem[]): boolean {
  if (items.length === 0) return false;
  return items.every((i) => i.type === 'repair' || i.type === 'fee');
}
